import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { BroadcastMessage, tabCommunication } from '../../../felles/TabCommunicationService';
import U from '../../../felles/Utils';
import UserCred from '../../../models/UserCred';
import * as actions from '../../../store/actions/index';
import { AppState } from '../../../store/rootReducer';
import LogoSimple from '../../components/SVGLogoSimple';
import { useStyles } from '../home/Home.styles';

/*
 *
 */
const Home = () => {
  //
  const dispatch = useDispatch();
  const open = useSelector((state) => (state as AppState).auth.isLoginDialogueOpen);
  const isLoginStateUI = useSelector((state) => (state as AppState).auth.loginStateUI);
  const { classes } = useStyles();
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const isLoading: boolean = useSelector((state) => (state as AppState).utils.isLoading);
  const isLogged: number = useSelector((state) => (state as AppState).auth.isLogged);
  const loginStateUI: number = useSelector((state) => (state as AppState).auth.loginStateUI);
  const tabListSize = useSelector((state: AppState) => state.auth.tabListSize);

  useEffect(() => {
    // Handler for token responses from other tabs
    const handleTokenResponse = (message: BroadcastMessage) => {
      if (message.command === 'TOKEN_RESPONSE' && message.data) {
        U.log('📨 Received TOKEN_RESPONSE from other tab');
        const { token, sessionData } = message.data;
        if (token && token.trim() !== '') {
          U.log('✅ Valid token received, restoring session and auto-logging in');
          // Restore session storage from other tab
          if (sessionData) {
            Object.keys(sessionData).forEach(key => {
              sessionStorage.setItem(key, sessionData[key]);
            });
          } else {
            // At minimum, set the token
            sessionStorage.setItem('token', token);
          }
          // Auto-login with the received token
          U.log('🔄 Dispatched auto-login action with token from other tab');
          if (token && token.trim() !== '' && isLogged === 0 && (loginStateUI === -1 || loginStateUI === 1)) {
            dispatch(actions.userAutologinToAutodataAuthGet(token, '', false, isLogged));
          }
        } else {
          U.log('⚠️ Received empty token from other tab');
        }
      }
    };

    // Handler for token requests from other tabs (respond if we have a token)
    const handleTokenRequest = (message: BroadcastMessage) => {
      if (message.command === 'TOKEN_REQUEST') {
        U.log('❓ Received TOKEN_REQUEST in Logout component');
        const currentToken = sessionStorage.getItem('token');
        U.log('Checking tokens - token:', !!currentToken, 'isLogged:', isLogged);

        // On logout page, respond if we have a token in sessionStorage (isLogged state might be 0)
        if (currentToken && currentToken.trim() !== '') {
          U.log('✅ Have valid token in sessionStorage, responding to TOKEN_REQUEST from Logout page');
          // Gather session storage data to send
          const sessionData: Record<string, string> = {};
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key) {
              sessionData[key] = sessionStorage.getItem(key) || '';
            }
          }
          tabCommunication.sendToken({
            token: currentToken,
            sessionData: sessionData
          });
        } else {
          U.log('⚠️ Cannot respond to TOKEN_REQUEST - no token in sessionStorage');
        }
      }
    };

    // Register message handlers
    tabCommunication.onMessage('TOKEN_RESPONSE', handleTokenResponse);
    tabCommunication.onMessage('TOKEN_REQUEST', handleTokenRequest);

    // When another tab logs in, request its token to auto-login this tab
    tabCommunication.onMessage('SESSION_LOGGED_IN', () => {
      U.log('📡 Received SESSION_LOGGED_IN from another tab');
      if (isLogged === 0 && (loginStateUI === -1 || loginStateUI === 1)) {
        U.log('🔄 Requesting token from logged-in tab for auto-login');
        tabCommunication.requestToken();
      }
    });

    // Request token from other tabs only for fresh tabs (tabListSize=0 means
    // tab coordination hasn't initialized yet, so this is a newly opened tab).
    // After logout, tabListSize >= 1 so we skip token requests to prevent auto-login.
    if (loginStateUI !== 2 && tabListSize === 0) {
      tabCommunication.requestToken();
    }
    //
    const token = sessionStorage.getItem('token');

    if (token && token.trim() !== '' && isLogged === 0) {
      dispatch(actions.setSessionUIState(1));
    } else if (tabListSize === 1) {
      dispatch(actions.setSessionUIState(-1));
    }

    if (document.getElementById('user-textfield') !== document.activeElement)
      document.getElementById('user-textfield')?.focus();

    // Cleanup
    return () => {
      tabCommunication.offMessage('TOKEN_RESPONSE');
      tabCommunication.offMessage('TOKEN_REQUEST');
      tabCommunication.offMessage('SESSION_LOGGED_IN');
    };
  }, [dispatch, isLogged, loginStateUI]);

  // When this tab successfully logs in, notify other tabs to auto-login
  useEffect(() => {
    if (isLogged === 1) {
      U.log('📡 Broadcasting SESSION_LOGGED_IN to other tabs');
      tabCommunication.broadcast({ command: 'SESSION_LOGGED_IN' });
    }
  }, [isLogged]);

  /******************************************************************************/
  const handleClose = () => {
    dispatch(actions.setLoginDialogOpeningState(false));
  };
  //
  function handleSubmit(event) {
    event.preventDefault();
    dispatch(actions.userLoginToAutodataGet(new UserCred(user, password, '', false)));
  }

  function handleRefresh() {
    const token = sessionStorage.getItem('token') || '';
    if (token && token.trim() !== '') {
      dispatch(actions.userAutologinToAutodataAuthGet(token, '', false, isLogged));
    } else {
      // Try to get token from another tab/window before falling back to login form
      tabCommunication.requestToken();
      setTimeout(() => {
        const tokenAfterRequest = sessionStorage.getItem('token') || '';
        if (!tokenAfterRequest || tokenAfterRequest.trim() === '') {
          dispatch(actions.setSessionUIState(-1));
        }
      }, 2000);
    }
  }

  function handleCloseWindow() {
    // Check if this window was opened by another window (has an opener)
    // window.opener will be non-null if opened via window.open()
    if (window.opener && !window.opener.closed) {
      // This is a child window, we can close it
      window.close();
    } else {
      // This is a regular tab/window (not opened by script)
      // Clear session and navigate to login
      sessionStorage.clear();
      window.location.href = U.HOMEPAGE_CONTEXT + '/';
    }
  }

  //function showLoginform() { dispatch(actions.setSessionUIState(-1));}
  /*
  isLoginStateUI === -1 is the FORM
  isLoginStateUI ===  0 autologin is in progress
  isLoginStateUI ===  1 all Tab logged out except this one
  isLoginStateUI ===  2 all Tab logged out 
  isLoginStateUI ===  3 already logged in from another tab
  */
  return (
    <>
      {isLoginStateUI === 0 ? (
        <>Initializing....</>
      ) : isLoginStateUI === 1 ? (
        <Dialog open={true} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          {/*<DialogTitle id="alert-dialog-title">{'Klikk Logg inn hvis du ønsker å logge inn igjen ,'}</DialogTitle>*/}
          <DialogTitle id="alert-dialog-title">{'Innlogging pågår...'}</DialogTitle>
            {/*<DialogContent><DialogContentText id="alert-dialog-description">
              eller lukk fanen / vinduet hvis du ikke lenger trenger det.
            </DialogContentText></DialogContent><DialogActions>
            <Button onClick={handleRefresh} color="primary" autoFocus>
              Logg inn
            </Button>
          </DialogActions>*/}
        </Dialog>
      ) : isLoginStateUI === 2 ? (
        <Dialog open={true} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">{'Denne økten har utløpt.'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Lukk denne fanen eller dette vinduet, og logg inn fra en ny fane..
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseWindow} color="secondary">
              Lukk vindu
            </Button>
          </DialogActions>
        </Dialog>
      ) : isLoginStateUI === -1 && isLoading ? (
        <>place holder....</>
      ) : isLoginStateUI === -1 && !isLoading ? (
        <div className={classes.backgroundImg}>
          <form className={classes.root} noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
              <DialogTitle id="alert-dialog-title">{'Opplysningene du oppga er feilaktige. '}</DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">Brukernavn og/eller passord er ikke korrekt. Forsøk igjen.</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="primary" autoFocus>
                  Ok
                </Button>
              </DialogActions>
            </Dialog>
            <div className="form-login__logo">
              <LogoSimple width="150" />
            </div>
            <div className="form-login__h1 centered">Auto Online</div>
            <div className="form-login__h2 marginTopBottom15">Velkommen</div> <div className="marginTopBottom5">Brukernavn</div>
            <div className="form-login__texfield">
              <TextField
                id="user-textfield"
                type="text"
                name="user"
                onInput={(e) => setUser((e.target as HTMLTextAreaElement).value)}
                placeholder=""
                variant="outlined"
                inputProps={{ tabIndex: 1 }}
              />
            </div>
            <div>
              <div className="row marginTopBottom5">
                {' '}
                <div className="column ">Passord</div>{' '}
                <div className="column right">
                  <NavLink to={U.HOMEPAGE_CONTEXT + '/passwordLinkRecovery'}>Glemt passord?</NavLink>
                </div>
              </div>
              <TextField
                type="password"
                name="password"
                onInput={(e) => setPassword((e.target as HTMLTextAreaElement).value)}
                placeholder=""
                variant="outlined"
                inputProps={{ tabIndex: 2 }}
              />
            </div>
            <div className="form-login__submit-button centered marginTopBottom15">
              <Button type="submit" variant="contained" color="primary" disableElevation tabIndex={3}>
                Logg inn
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <>....</>
      )}
    </>
  );
};

export default Home;
